import { buildExternalId, CanonicalLoad, LoadSourceType, stripExternalIdPrefix } from '@numeo/types-core'

// Default coordinates (center of US - Lebanon, Kansas) used when coordinates are missing
const DEFAULT_LAT = 39.8283
const DEFAULT_LON = -98.5795

/**
 * Fold legacy `DAT_EXTENSION` to `DAT`. All DAT load data now lives under the
 * `DAT` source, but loads streamed straight from the Chrome extension still
 * carry `DAT_EXTENSION` on `sourceType`/`externalId`/`dedupSig`. Emitting that
 * stale label makes the backend look the load up under `DAT_EXTENSION|{id}` —
 * which misses the row persisted as `DAT|{id}` — and email-service then fails
 * with "Load not found". Normalize every source-bearing field before sending.
 */
const normalizeSourceType = (sourceType: LoadSourceType): LoadSourceType => (sourceType === 'DAT_EXTENSION' ? 'DAT' : sourceType)

/**
 * Resolve the composite `SOURCE|rawId` external id emitted to the v4 endpoint.
 * Loads with no externalId fall back to `PRIVATE|{id}`.
 */
const resolveExternalId = (load: CanonicalLoad): string => {
    if (!load.externalId) return `PRIVATE|${load.id}`
    return buildExternalId(normalizeSourceType(load.sourceType), stripExternalIdPrefix(load.externalId))
}

/**
 * The extension sets `dedupSig` to the raw `DAT_EXTENSION|{id}` string rather
 * than a real hash. Drop it for DAT_EXTENSION loads so the backend recomputes a
 * proper SHA256 dedupSig (matching server-ingested DAT loads); pass through
 * otherwise.
 */
const resolveDedupSig = (load: CanonicalLoad): string | undefined => (load.sourceType === 'DAT_EXTENSION' ? undefined : load.dedupSig)

/**
 * Convert CanonicalLoad to v4 API payload format
 * The v4 endpoint expects canonical format with flat fields
 */
export const convertCanonicalLoadToV4Payload = (load: CanonicalLoad) => {
    const externalId = resolveExternalId(load)

    const pickupLat = load.pickupLat ?? DEFAULT_LAT
    const pickupLon = load.pickupLon ?? DEFAULT_LON
    const deliveryLat = load.deliveryLat ?? load.pickupLat ?? DEFAULT_LAT
    const deliveryLon = load.deliveryLon ?? load.pickupLon ?? DEFAULT_LON

    const rateAmount = typeof load.rateAmountUsd === 'string' ? parseFloat(load.rateAmountUsd) : load.rateAmountUsd || 0

    return {
        externalId,
        dedupSig: resolveDedupSig(load),

        pickupCity: load.pickupCity || '',
        pickupStateProv: load.pickupStateProv || '',
        pickupPostalCode: load.pickupPostalCode || null,
        pickupCountryCode: load.pickupCountryCode || 'US',
        pickupLat,
        pickupLon,

        deliveryCity: load.deliveryCity || '',
        deliveryStateProv: load.deliveryStateProv || '',
        deliveryPostalCode: load.deliveryPostalCode || null,
        deliveryCountryCode: load.deliveryCountryCode || 'US',
        deliveryLat,
        deliveryLon,

        pickupWindowStart: load.pickupWindowStart || new Date().toISOString(),
        pickupWindowEnd: load.pickupWindowEnd || load.pickupWindowStart || new Date().toISOString(),
        deliveryWindowStart: load.deliveryWindowStart || null,
        deliveryWindowEnd: load.deliveryWindowEnd || null,

        equipmentType: load.equipmentType || '',
        equipmentLengthFt: load.equipmentLengthFt,
        fullPartial: load.fullPartial || '',
        weightLbs: load.weightLbs,

        distanceMiles: load.distanceMiles,
        rateAmountUsd: rateAmount,
        ratePerMileUsd: load.ratePerMileUsd || null,
        rateBasis: load.rateBasis || '',

        brokerCompanyName: load.brokerCompanyName || '',
        brokerMc: load.brokerMc || null,
        brokerDot: load.brokerDot || null,
        brokerContactName: load.brokerContactName || null,
        brokerContactEmail: load.brokerContactEmail || load.posterContactEmail || '',
        brokerContactPhone: load.brokerContactPhone || load.posterContactPhone || null,

        comment: load.comment || null,
        commodity: load.commodity || null,
        specialRequirements: load.specialRequirements || null,

        sourceType: normalizeSourceType(load.sourceType),
    }
}
